<template>
    <div :class="startOpen ? '' : 'mt-8'">
        <div v-if="!startOpen">
            <!-- Separator -->
            <hr class="border-gray-200 my-6" />

            <div class="flex justify-between items-center mb-4">
                <h3 class="text-gray-700 text-base font-bold font-slab">
                    Team Bot Settings
                </h3>
                <!-- Add Bot Button below the list, only if no bot exists and user has management permission -->
                <button
                    v-if="canManage && !bot"
                    type="button"
                    @click="openAddModal"
                    class="
                        bg-purple-600
                        text-white text-xs
                        px-3
                        py-1.5
                        rounded-lg
                        hover:bg-purple-700
                        font-semibold
                        flex
                        items-center
                        gap-1
                        transition
                    "
                >
                    <ion-icon
                        name="add-circle-outline"
                        class="text-sm"
                    ></ion-icon>
                    Add Bot
                </button>
            </div>

            <!-- Bot Card, shown if bot exists -->
            <div
                v-if="bot"
                class="
                    relative
                    bg-white
                    my-4
                    rounded-md
                    p-6
                    border border-purple-200
                "
            >
                <!-- Context Menu for Bot -->
                <div v-if="canManage" class="absolute right-4 top-4 z-10">
                    <button
                        type="button"
                        @click="isDropdownOpen = !isDropdownOpen"
                        class="
                            p-1.5
                            hover:bg-gray-100
                            rounded-full
                            text-gray-500
                            transition
                            flex
                            items-center
                            justify-center
                            border border-gray-200
                        "
                    >
                        <ion-icon
                            name="ellipsis-vertical-outline"
                            class="text-lg"
                        ></ion-icon>
                    </button>
                    <div
                        v-if="isDropdownOpen"
                        class="
                            absolute
                            right-0
                            mt-1
                            bg-white
                            border border-gray-200
                            rounded-lg
                            shadow-lg
                            py-1
                            w-36
                            text-sm
                        "
                    >
                        <button
                            type="button"
                            @click="openEditModal"
                            class="
                                w-full
                                text-left
                                px-4
                                py-2
                                hover:bg-gray-100
                                flex
                                items-center
                                gap-2
                            "
                        >
                            <ion-icon name="create-outline"></ion-icon>
                            Edit Bot
                        </button>
                        <button
                            type="button"
                            @click="openDeleteModal"
                            class="
                                w-full
                                text-left
                                px-4
                                py-2
                                hover:bg-gray-100
                                text-red-600
                                flex
                                items-center
                                gap-2
                            "
                        >
                            <ion-icon name="trash-outline"></ion-icon>
                            Remove Bot
                        </button>
                    </div>
                </div>

                <!-- Bot Card Content -->
                <div
                    class="
                        flex-col
                        md:flex-row
                        flex-grow-1 flex-shrink-1 flex
                        items-center
                        gap-3
                    "
                >
                    <img
                        :src="botAvatarUrl"
                        alt="Bot Avatar"
                        class="
                            rounded-full
                            w-[54px]
                            h-[54px]
                            object-cover
                            aspect-square
                        "
                    />

                    <div class="text-center md:text-left">
                        <div class="text-lg font-bold">{{ bot.name }}</div>
                        <div class="text-sm opacity-50">
                            Provider: {{ displayProvider }}
                        </div>
                        <div
                            v-if="bot && bot.total_tokens_used !== undefined"
                            class="text-sm opacity-50"
                        >
                            Total Token Usage:
                            <strong class="text-purple-600">{{
                                bot.total_tokens_used.toLocaleString()
                            }}</strong>
                            tokens
                        </div>
                        <div
                            class="
                                text-xs
                                py-1
                                px-2
                                rounded-lg
                                mt-1.5
                                bg-purple-600
                                text-white
                                inline-block
                                font-semibold
                            "
                        >
                            Team Bot
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Add/Edit Bot Modal (using ModalCustom) -->
        <ModalCustom
            :is-open="isModalOpen"
            :title="modalTitle"
            @close="closeModal"
            :z-index="1001"
        >
            <form
                :action="saveRoute"
                method="POST"
                enctype="multipart/form-data"
                class="w-[50vw] min-w-[320px] max-w-full"
            >
                <!-- CSRF Token -->
                <input type="hidden" name="_token" :value="csrfToken" />
                <input type="hidden" name="team_id" :value="teamId" />

                <div class="space-y-4 my-4 text-left">
                    <!-- Token Usage Indicator -->
                    <div
                        v-if="bot && bot.total_tokens_used !== undefined"
                        class="
                            bg-purple-50
                            border border-purple-100
                            rounded-lg
                            p-3
                            text-sm text-purple-800
                            flex
                            justify-between
                            items-center
                        "
                    >
                        <span>Total Token Usage:</span>
                        <strong class="text-base font-bold text-purple-700"
                            >{{
                                bot.total_tokens_used.toLocaleString()
                            }}
                            tokens</strong
                        >
                    </div>

                    <!-- Name Field -->
                    <div>
                        <label
                            for="bot_name"
                            class="block text-sm font-semibold text-gray-700"
                            >Name</label
                        >
                        <input
                            type="text"
                            name="name"
                            id="bot_name"
                            v-model="form.name"
                            required
                            class="
                                mt-1
                                block
                                w-full
                                border border-gray-300
                                rounded-md
                                shadow-sm
                                py-2
                                px-3
                                focus:outline-none
                                focus:ring-purple-500
                                focus:border-purple-500
                                sm:text-sm
                            "
                        />
                    </div>

                    <!-- Profile Picture Field -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700"
                            >Profile Picture</label
                        >
                        <div class="mt-1 flex items-center gap-4">
                            <img
                                :src="avatarPreviewUrl"
                                alt="Preview"
                                class="
                                    h-12
                                    w-12
                                    rounded-full
                                    object-cover
                                    border border-gray-300
                                "
                            />
                            <input
                                type="file"
                                name="bot_image"
                                accept="image/*"
                                @change="onFileChange"
                                class="
                                    block
                                    w-full
                                    text-sm text-gray-500
                                    file:mr-4
                                    file:py-2
                                    file:px-4
                                    file:rounded-md
                                    file:border-0
                                    file:text-sm
                                    file:font-semibold
                                    file:bg-purple-50
                                    file:text-purple-700
                                    hover:file:bg-purple-100
                                "
                            />
                        </div>
                    </div>

                    <!-- Provider Field -->
                    <div>
                        <label
                            for="bot_provider"
                            class="block text-sm font-semibold text-gray-700"
                            >Provider</label
                        >
                        <select
                            id="bot_provider"
                            name="provider"
                            v-model="form.provider"
                            class="
                                mt-1
                                block
                                w-full
                                border border-gray-300
                                rounded-md
                                shadow-sm
                                py-2
                                px-3
                                focus:outline-none
                                focus:ring-purple-500
                                focus:border-purple-500
                                sm:text-sm
                                bg-white
                            "
                        >
                            <option value="platform_default">
                                Platform Default
                            </option>
                            <option value="openai">OpenAI</option>
                            <option value="gemini">Gemini</option>
                        </select>
                    </div>

                    <!-- API Key Field -->
                    <div v-if="form.provider !== 'platform_default'">
                        <label
                            for="bot_api_key"
                            class="block text-sm font-semibold text-gray-700"
                            >API Key</label
                        >
                        <input
                            type="text"
                            name="api_key"
                            id="bot_api_key"
                            v-model="form.api_key"
                            class="
                                mt-1
                                block
                                w-full
                                border border-gray-300
                                rounded-md
                                shadow-sm
                                py-2
                                px-3
                                focus:outline-none
                                focus:ring-purple-500
                                focus:border-purple-500
                                sm:text-sm
                            "
                        />
                    </div>

                    <!-- Base formation / defense defaults -->
                    <div class="grid grid-cols-2 gap-3">
                        <div>
                            <label
                                class="
                                    block
                                    text-sm
                                    font-semibold
                                    text-gray-700
                                "
                                >Base Offense Formation</label
                            >
                            <select
                                name="base_formation"
                                v-model="form.base_formation"
                                class="
                                    mt-1
                                    block
                                    w-full
                                    border border-gray-300
                                    rounded-md
                                    shadow-sm
                                    py-2
                                    px-3
                                    focus:outline-none
                                    focus:ring-purple-500
                                    focus:border-purple-500
                                    sm:text-sm
                                "
                            >
                                <option value="">— none —</option>
                                <option
                                    v-for="f in offenseFormations"
                                    :key="f"
                                    :value="f"
                                >
                                    {{ f }}
                                </option>
                            </select>
                        </div>
                        <div>
                            <label
                                class="
                                    block
                                    text-sm
                                    font-semibold
                                    text-gray-700
                                "
                                >Base Defense</label
                            >
                            <select
                                name="base_defense"
                                v-model="form.base_defense"
                                class="
                                    mt-1
                                    block
                                    w-full
                                    border border-gray-300
                                    rounded-md
                                    shadow-sm
                                    py-2
                                    px-3
                                    focus:outline-none
                                    focus:ring-purple-500
                                    focus:border-purple-500
                                    sm:text-sm
                                "
                            >
                                <option value="">— none —</option>
                                <option
                                    v-for="d in defenseFormations"
                                    :key="d"
                                    :value="d"
                                >
                                    {{ d }}
                                </option>
                            </select>
                        </div>
                    </div>

                    <!-- Coaching instructions (rich editor with / autocomplete) -->
                    <div>
                        <label class="block text-sm font-semibold text-gray-700"
                            >Coaching Instructions</label
                        >
                        <coaching-instructions-editor
                            name="custom_instructions"
                            :value="form.custom_instructions"
                        />
                    </div>
                </div>

                <div
                    class="
                        flex
                        justify-end
                        gap-2
                        border-t border-gray-200
                        pt-3
                        mt-4
                    "
                >
                    <button
                        type="button"
                        @click="closeModal"
                        class="
                            rounded-md
                            border border-gray-300
                            shadow-sm
                            px-4
                            py-2
                            bg-white
                            text-sm
                            font-semibold
                            text-gray-700
                            hover:bg-gray-50
                        "
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        class="
                            rounded-md
                            border border-transparent
                            shadow-sm
                            px-4
                            py-2
                            bg-purple-600
                            text-sm
                            font-semibold
                            text-white
                            hover:bg-purple-700
                        "
                    >
                        Save
                    </button>
                </div>
            </form>
        </ModalCustom>

        <!-- Delete Bot Modal (using ModalCustom) -->
        <ModalCustom
            :is-open="isDeleteModalOpen"
            title="Remove Bot from Team"
            @close="isDeleteModalOpen = false"
        >
            <form
                :action="deleteRoute"
                method="POST"
                class="w-[30vw] min-w-[320px] max-w-full text-left my-4"
            >
                <input type="hidden" name="_token" :value="csrfToken" />
                <input type="hidden" name="team_id" :value="teamId" />

                <div
                    class="
                        bg-gray-100
                        text-gray-800
                        py-4
                        px-6
                        rounded-lg
                        text-sm
                        mb-4
                    "
                >
                    Are you sure you want to remove the team bot
                    <strong>{{ bot ? bot.name : "" }}</strong
                    >?
                </div>

                <div class="flex items-center gap-2 mb-4">
                    <input
                        type="checkbox"
                        id="confirm_bot_delete"
                        v-model="confirmDelete"
                        required
                    />
                    <label for="confirm_bot_delete" class="text-sm select-none">
                        I understand, remove this bot.
                    </label>
                </div>

                <div
                    class="
                        flex
                        justify-end
                        gap-2
                        border-t border-gray-200
                        pt-3
                        mt-4
                    "
                >
                    <button
                        type="button"
                        @click="isDeleteModalOpen = false"
                        class="
                            rounded-md
                            border border-gray-300
                            shadow-sm
                            px-4
                            py-2
                            bg-white
                            text-sm
                            font-semibold
                            text-gray-700
                            hover:bg-gray-50
                        "
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        :disabled="!confirmDelete"
                        class="
                            rounded-md
                            border border-transparent
                            shadow-sm
                            px-4
                            py-2
                            bg-red-600
                            text-sm
                            font-semibold
                            text-white
                            hover:bg-red-700
                            disabled:opacity-50
                        "
                    >
                        Remove Bot
                    </button>
                </div>
            </form>
        </ModalCustom>
    </div>
</template>

<script>
import ModalCustom from "./ModalCustom.vue";

export default {
    name: "TeamBotManager",
    components: {
        ModalCustom,
    },
    props: {
        teamId: {
            required: true,
        },
        saveRoute: {
            type: String,
            required: true,
        },
        deleteRoute: {
            type: String,
            required: true,
        },
        canManage: {
            type: Boolean,
            default: false,
        },
        initialBot: {
            type: Object,
            default: null,
        },
        defaultAvatar: {
            type: String,
            default: "/default-images/avatar.png",
        },
        startOpen: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            bot: this.initialBot,
            isDropdownOpen: false,
            isModalOpen: false,
            isDeleteModalOpen: false,
            modalTitle: "Add Bot",
            confirmDelete: false,
            form: {
                name: "",
                provider: "platform_default",
                api_key: "",
                custom_instructions: "",
                base_formation: "",
                base_defense: "",
            },
            offenseFormations: [
                "I-Formation",
                "Shotgun",
                "Single Back",
                "Spread",
                "Trips",
            ],
            defenseFormations: ["4-3", "3-4", "4-2-5", "Dime"],
            avatarFile: null,
            avatarPreview: null,
            csrfToken:
                document.querySelector('meta[name="csrf-token"]')?.content ||
                "",
        };
    },
    mounted() {
        if (this.startOpen) {
            if (this.bot) {
                this.openEditModal();
            } else {
                this.openAddModal();
            }
        }
    },
    computed: {
        botAvatarUrl() {
            if (!this.bot) return this.defaultAvatar;
            if (this.bot.avatar) {
                return this.bot.avatar;
            }
            return this.defaultAvatar;
        },
        avatarPreviewUrl() {
            if (this.avatarPreview) return this.avatarPreview;
            return this.botAvatarUrl;
        },
        displayProvider() {
            if (!this.bot) return "";
            if (this.bot.provider === "platform_default")
                return "Platform Default";
            if (this.bot.provider === "openai") return "OpenAI";
            if (this.bot.provider === "gemini") return "Gemini";
            return this.bot.provider;
        },
    },
    methods: {
        openAddModal() {
            this.modalTitle = "Add Bot";
            this.form.name = "";
            this.form.provider = "platform_default";
            this.form.api_key = "";
            this.form.custom_instructions = "";
            this.form.base_formation = "";
            this.form.base_defense = "";
            this.avatarFile = null;
            this.avatarPreview = null;
            this.isModalOpen = true;
        },
        openEditModal() {
            this.modalTitle = "Edit Bot";
            this.form.name = this.bot.name;
            this.form.provider = this.bot.provider;
            this.form.api_key = this.bot.api_key || "";
            this.form.custom_instructions = this.bot.custom_instructions || "";
            this.form.base_formation = this.bot.base_formation || "";
            this.form.base_defense = this.bot.base_defense || "";
            this.avatarFile = null;
            this.avatarPreview = null;
            this.isDropdownOpen = false;
            this.isModalOpen = true;
        },
        openDeleteModal() {
            this.confirmDelete = false;
            this.isDropdownOpen = false;
            this.isDeleteModalOpen = true;
        },
        closeModal() {
            this.isModalOpen = false;
            this.$emit("close");
        },
        onFileChange(e) {
            const file = e.target.files[0];
            if (file) {
                this.avatarFile = file;
                const reader = new FileReader();
                reader.onload = (event) => {
                    this.avatarPreview = event.target.result;
                };
                reader.readAsDataURL(file);
            }
        },
    },
};
</script>
